import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import { ReactMultiEmail } from 'react-multi-email';
import 'react-multi-email/dist/style.css';
import TextField from "@mui/material/TextField";
import {
  Backdrop,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  Paper,
  Tooltip,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { toast } from "react-toastify";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import ErrorAlert from "helper/errorAlert";
import { useNavigate } from "react-router-dom";
import RadioButtonGroup from "./invoiceOption";
import { useApiClient } from "context/ApiClientContext";
import InfoIcon from "@mui/icons-material/Info";

export default function FormDialog({ companyData, id, handleShowForm }) {
  const navigate = useNavigate();

  const [mode, setMode] = useState("edit");
  const [readOnly, setReadOnly] = useState(false);
  const [emails, setEmails] = useState([]);
  const [isFocused, setIsFocused] = useState(false);
  const [inputForm, setInputForm] = useState(
    companyData || {
      name: "",
      code: "",
      signature: "",
      token: "",
      isDefault: false,
      isActive: true,
      isGlobal: true,
      epicor_ApiKey: "",
      epicor_RestUrl: "",
      epicor_UserName: "",
      epicor_Password: "",
      syteline_SQLServer: "",
      syteline_SQLUser: "",
      syteline_SQLPass: "",
      syteline_SQLDbName: "",
      erp_Opt: "",
      minutes_interval: 0,
      days_interval: 0,
      months_interval: 0,
      mqhost: "",
      emailreceiver: "",
      invnumsource: "Invoice Number",
      tin_number: "",
      intermediary: false,
    }
  );
  const apiClient = useApiClient();

  const [errors, setErrors] = useState({
    name: "",
    code: "",
    irbsecret: "",
    irbclientid: "",
    emailreceiver: "",
    tin_number: ""
  });

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };

  const [otherFieldError, setOtherFieldError] = useState(false);

  const validate = (data, value) => {
    switch (data) {
      case 'emailreceiver':
        if (value.length === 0) return 'Email is required';
        for (const email of value.split('; ')) {
          if (!/\S+@\S+\.\S+/.test(email)) return 'One or more emails are invalid';
        }
        return '';
        default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!inputForm.name) newErrors.name = "Name is required";
    if (!inputForm.code) {
      newErrors.code = "ERP Id is required";
    }
    if (!inputForm.tin_number) {
      newErrors.tin_number = "Tin Number is required";
    }
    if (otherFieldError) {
      newErrors.invnumsource = "Invoice number source is invalid";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (id) {
      setMode("edit");
      getOne(id);
      setReadOnly(false);
    } else {
      setMode("create");
    }
  }, []);

  useEffect(() => {
    if (inputForm.emailreceiver) {
      setEmails(inputForm.emailreceiver.split('; '));
    }
  }, [inputForm.emailreceiver]);

  const getOne = async (id) => {
    try {
      const response = await axios.get(
        `${apiClient}/company/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            Accept: "application/json",
          },
        }
      );

      setInputForm(response.data);

    } catch (error) {
      console.error("An error occurred:", error.response);
    }
  };

  const handleCreate = (property, event) => {
    const value =
      event.target.type === "checkbox"
        ? event.target.checked
        : event.target.value;

    setInputForm((prevState) => ({
      ...prevState,
      [property]: value,
    }));

    const error = validate(property, value);
    setErrors({ ...errors, [property]: error });
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      handleOpen();

      const toastId = toast.loading('Saving data...', {
        position: 'top-right',
      });

      try {
        await new Promise(resolve => setTimeout(resolve, 3000));

        let response = '';
        switch (mode) {
          case 'create':

            response = await axios.post(
              `${apiClient}/company`,
              { ...inputForm, emailreceiver: emails.join('; ') },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  Accept: 'application/json',
                },
              }
            );
            break;
          case 'edit':

            response = await axios.put(
              `${apiClient}/company/${id}`,
              { ...inputForm, emailreceiver: emails.join('; ') },
              {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`,
                  Accept: 'application/json',
                },
              }
            );
            break;
          default:
            return;
        }
        // handleClose();
        // Update the toast with success message
        toast.update(toastId, {
          render: mode === 'create' ? 'Successfully created.' : 'Successfully updated.',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });

        setTimeout(() => {
          handleShowForm();
        }, 500);
      } catch (error) {
        let errorMessage = 'An error occurred. Please try again.';
        if (error?.response?.data) {
          // Check if the error data is an array and has at least one element
          if (Array.isArray(error.response.data) && error.response.data.length > 0) {
            errorMessage = error.response.data[0];
          } else if (typeof error.response.data === 'string') {
            // Handle the case where error data is a string message
            errorMessage = error.response.data;
          } else if (typeof error.response.data.message === 'string') {
            // Handle the case where error data has a message field
            errorMessage = error.response.data.message;
          }
        }

        // ErrorAlert(error, navigate);
        // Update the toast with error message
        toast.update(toastId, {
          render: errorMessage,
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
      } finally {
        // setTimeout(() => {
        handleClose();
        // }, 1000);
      }
    }
  };

  const setEdit = () => {
    if (mode === "view") {
      setMode("edit");
      setReadOnly(false);
    }
  };

  return (
    <Grid
      container
      spacing={5}
      mt={1}
      display="flex"
      justifyContent="center"
    >
      <Grid item xs={12} lg={8}>
        <div>
          <Paper sx={{ padding: "20px" }}>
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={open}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <span>
                <IconButton sx={{ padding: 0, marginBottom: "5px" }} onClick={handleShowForm}>
                  <Tooltip title="Back to Listing" placement="right">
                    <KeyboardBackspaceIcon />
                  </Tooltip>
                </IconButton>
              </span>
            </Box>
            <Box p={3}>
              <TextField
                error={!!errors.name}
                helperText={errors.name}
                onChange={(event) => handleCreate("name", event)}
                value={inputForm.name}
                required
                margin="dense"
                id="name"
                name="name"
                label="Name"
                type="text"
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: readOnly,
                }}
              />
              <TextField
                error={!!errors.code}
                helperText={errors.code}
                onChange={(event) => handleCreate("code", event)}
                value={inputForm.code}
                required
                margin="dense"
                id="code"
                name="code"
                label="ERP ID"
                type="text"
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: readOnly,
                  disabled: mode === "edit",
                }}
              />
              <TextField
                error={!!errors.irbclientid}
                helperText={errors.irbclientid}
                onChange={(event) => handleCreate("irbclientid", event)}
                value={inputForm.irbclientid}
                margin="dense"
                id="irbclientid"
                name="irbclientid"
                label="Client Id"
                type="text"
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: readOnly,
                }}
              />
              <TextField
                error={!!errors.irbsecret}
                helperText={errors.irbsecret}
                onChange={(event) => handleCreate("irbsecret", event)}
                value={inputForm.irbsecret}
                margin="dense"
                id="irbsecret"
                name="irbsecret"
                label="Client Secret"
                type="text"
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: readOnly,
                }}
              />
              <TextField
                error={!!errors.tin_number}
                helperText={errors.tin_number}
                onChange={(event) => handleCreate("tin_number", event)}
                value={inputForm.tin_number}
                margin="dense"
                id="tin_number"
                name="tin_number"
                label="Tin Number"
                type="text"
                fullWidth
                variant="outlined"
                InputProps={{
                  readOnly: readOnly,
                }}
              />
              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel
                  htmlFor="email-receiver"
                  shrink={emails.length > 0 || !!inputForm.emailreceiver || isFocused}
                  sx={{
                    backgroundColor: 'white',
                    padding: '0 4px',
                    transform: emails.length > 0 || !!inputForm.emailreceiver || isFocused ? 'translate(14px, -6px) scale(0.75)' : 'translate(14px, 16px) scale(1)',
                    transition: 'transform 0.2s ease-in-out'
                  }}
                >
                  Email Receiver
                </InputLabel>
                <Box
                  sx={{
                    '& .react-multi-email': {
                      width: '100%',
                      padding: '12.5px 14px',
                      marginBottom: '8px',
                      '&:hover': {
                        borderColor: 'rgba(0, 0, 0, 0.87)',
                      },
                      '&:focus-within': {
                        borderColor: '#3f51b5',
                        boxShadow: '0 0 0 1px #3f51b5',
                      },
                    },
                    '& .react-multi-email input': {
                      border: 'none',
                      outline: 'none',
                      width: '100%',
                    },
                  }}
                >
                  <ReactMultiEmail
                    emails={emails}
                    onChange={(updatedEmails) => {
                      setEmails(updatedEmails);
                      handleCreate('emailreceiver', {
                        target: { value: updatedEmails.join('; ') },
                      });
                    }}
                    getLabel={(email, index, removeEmail) => (
                      <div data-tag key={index}>
                        <div data-tag-item style={{ fontSize: '17px' }}>{email}</div>
                        <span data-tag-handle onClick={() => removeEmail(index)}>
                          ×
                        </span>
                      </div>
                    )}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                  />
                </Box>
              </FormControl>
              <Grid container spacing={3}>
                <Grid item xs={12} my={3}>
                  <Grid container direction="column" alignItems="flex-start">
                    <Grid item>
                      <RadioButtonGroup
                        apiSetting={inputForm}
                        updateApiSetting={(updatedData) => {
                          setInputForm(updatedData);
                          // setChangesMade(true);
                        }}
                        onValidationChange={setOtherFieldError}
                      />
                    </Grid>
                    <Grid item>
                      <FormGroup>
                        <FormControlLabel
                          checked={inputForm.intermediary}
                          control={<Checkbox />}
                          label={
                            <div style={{ display: "flex", alignItems: "center" }}>
                              Set company as intermediary
                              <Tooltip 
                                title="Only one (1) company can be set as intermediary. Once this company is set as intermediary, other company will be set as false" 
                                placement="right"
                              >
                                <IconButton 
                                  color="primary" 
                                  style={{ marginLeft: 4 }}
                                >
                                  <InfoIcon />
                                </IconButton>
                              </Tooltip>
                            </div>
                          }
                          disabled={readOnly}
                          onChange={(event) => handleCreate("intermediary", event)}
                        />
                      </FormGroup>
                    </Grid>
                    <Grid item>
                      <FormGroup>
                        <FormControlLabel
                          checked={inputForm.isActive}
                          control={<Checkbox />}
                          label="Set as active company"
                          disabled={readOnly}
                          onChange={(event) => handleCreate("isActive", event)}
                        />
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid container spacing={3}>

              </Grid>

            </Box>
              <Box display="flex" justifyContent="end" sx={{ marginTop: "-30px", marginRight: "25px" }}>
                <Button variant="contained" onClick={handleSubmit}>
                  Save
                </Button>
              </Box>
          </Paper>
        </div>
      </Grid>
    </Grid>
  );
}